import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { BATTERY_STATUS } from 'constants.js';
import API from 'api/index.js';

const initialState = {
    batteryLocation: null,
    data: null,
    drafts: [],
    importer: null,
    isConfirmed: false,
    isContinuingMultiorder: null,
    isDone: false,
    isFactorySkipped: null,
    isLocationConfirmed: false,
    isRecipientConfirmed: false,
    isShopConfirmed: false,
    isWhole: null,
    operator: null,
    recipientType: null,
    shops: [],
};

export const operatorBatterySlice = createSlice({
    name: 'operatorBattery',
    initialState,
    reducers: {
        reset: state => {
            // Resets everything but `drafts`, for when a user wants to add a new battery to the order
            state.batteryLocation = initialState.batteryLocation;
            state.data = initialState.data;
            state.importer = initialState.importer;
            state.isConfirmed = initialState.isConfirmed;
            state.isContinuingMultiorder = initialState.isContinuingMultiorder;
            state.isDone = initialState.isDone;
            state.isFactorySkipped = initialState.isFactorySkipped;
            state.isLocationConfirmed = initialState.isLocationConfirmed;
            state.isRecipientConfirmed = initialState.isRecipientConfirmed;
            state.isShopConfirmed = initialState.isShopConfirmed;
            state.isWhole = initialState.isWhole;
            state.operator = initialState.operator;
            state.recipientType = initialState.recipientType;
            state.shops = initialState.shops;
        },
        setBatteryData: (state, { payload }) => { state.data = payload; },
        setBatteryLocation: (state, { payload }) => { state.batteryLocation = payload; },
        setImporter: (state, { payload }) => { state.importer = payload; },
        setIsConfirmed: (state, { payload }) => { state.isConfirmed = payload; },
        setIsContinuingMultiorder: (state, { payload }) => { state.isContinuingMultiorder = payload; },
        setIsDone: (state, { payload }) => { state.isDone = payload; },
        setIsLocationConfirmed: (state, { payload }) => {
            state.isLocationConfirmed = payload;

            // Going back to the location invalidates the freight prices calculated from the old one
            if(!payload) state.shops = initialState.shops;
        },
        setIsRecipientConfirmed: (state, { payload }) => { state.isRecipientConfirmed = payload; },
        setIsShopConfirmed: (state, { payload }) => { state.isShopConfirmed = payload; },
        setIsWhole: (state, { payload }) => { state.isWhole = payload; },
        setOperator: (state, { payload }) => { state.operator = payload; },
        setRecipientType: (state, { payload }) => { state.recipientType = payload; },
    },
    extraReducers: builder => {
        const {
            batteryProviderShopSearch,
            batteryImporterShopSearch,
            batteryProviderImporterSearch,
            batteryProviderBatteryCreate,
            batteryProviderBatteryBatchList,
            batteryProviderBatteryBatchUpdate,
            batteryImporterBatteryBatchList,
            batteryImporterBatteryBatchUpdate,
        } = API.endpoints;

        builder
            .addMatcher(
                isAnyOf(
                    batteryProviderShopSearch.matchFulfilled,
                    batteryImporterShopSearch.matchFulfilled,
                ),
                (state, { payload }) => { state.shops = payload || []; },
            )
            .addMatcher(
                batteryProviderImporterSearch.matchFulfilled,
                (state, { payload }) => { state.importer = payload; },
            )
            .addMatcher(batteryProviderBatteryCreate.matchFulfilled, (state, { payload }) => {
                if([BATTERY_STATUS.PROVIDER_DRAFT, BATTERY_STATUS.IMPORTER_DRAFT].includes(payload?.status)) {
                    state.drafts = [...state.drafts, payload];
                }
            })
            .addMatcher(
                isAnyOf(
                    batteryProviderBatteryBatchList.matchFulfilled,
                    batteryImporterBatteryBatchList.matchFulfilled,
                ),
                (state, { payload }) => { state.drafts = state.isDone ? [] : (payload || []); },
            )
            .addMatcher(
                isAnyOf(
                    batteryProviderBatteryBatchUpdate.matchFulfilled,
                    batteryImporterBatteryBatchUpdate.matchFulfilled,
                ),
                state => {
                    state.drafts = [];
                    state.isDone = true;
                },
            );
    },
});

export const {
    reset,
    setBatteryData,
    setBatteryLocation,
    setImporter,
    setIsConfirmed,
    setIsContinuingMultiorder,
    setIsDone,
    setIsLocationConfirmed,
    setIsRecipientConfirmed,
    setIsShopConfirmed,
    setIsWhole,
    setOperator,
    setRecipientType,
} = operatorBatterySlice.actions;

export default operatorBatterySlice.reducer;
